import {
  Clock,
  Mail,
  MessageCircle,
  MessagesSquare,
  Phone,
  Users,
  Video,
} from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Skeleton } from "@/components/ui/skeleton";
import { useConversazioni } from "@/hooks/useConversazioni";
import type { Interlocuzione, InterlocTipo } from "@/types/api";

const TIPI_ICON: Record<InterlocTipo, typeof Phone> = {
  chiamata_voce: Phone,
  videocall: Video,
  email: Mail,
  incontro_presenza: Users,
  messaggio_chat: MessageCircle,
  whatsapp: MessagesSquare,
};

const TIPI_LABEL: Record<InterlocTipo, string> = {
  chiamata_voce: "Chiamata",
  videocall: "Videocall",
  email: "Email",
  incontro_presenza: "Incontro in presenza",
  messaggio_chat: "Chat",
  whatsapp: "WhatsApp",
};

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ConversazioniPage() {
  const { data: interlocuzioni = [], isLoading } = useConversazioni();

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Sezione</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Conversazioni
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Registro delle interlocuzioni del progetto: chiamate, email, videocall, incontri. Una memoria condivisa per ricostruire decisioni e contesto.
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : interlocuzioni.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          title="Nessuna conversazione registrata"
          description="Quando Maitime registrerà le interlocuzioni del progetto, le troverai qui."
        />
      ) : (
        <div className="space-y-2">
          {interlocuzioni.map((i) => (
            <InterlocCard key={i.id} item={i} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function InterlocCard({ item: i }: { item: Interlocuzione }) {
  const Icon = TIPI_ICON[i.tipo] ?? MessageCircle;
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-full bg-muted/60 flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{TIPI_LABEL[i.tipo]}</span>
            <span>·</span>
            <span>{formatDataOra(i.data)}</span>
            {i.durata_minuti != null && (
              <>
                <span>·</span>
                <span>
                  <Clock className="size-3 inline mr-0.5" />
                  {i.durata_minuti} min
                </span>
              </>
            )}
          </div>
          <h3 className="font-semibold text-foreground mt-0.5">{i.titolo}</h3>
          {i.sintesi && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
              {i.sintesi}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
